import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { formatCompact, formatNumber } from '../format';
import Legend from './Legend';

const MARGIN = { top: 22, right: 8, bottom: 26 };
const GAP = 2;          // spazio tra i segmenti impilati
const MAX_BAR = 24;     // spessore massimo delle colonne
const CHAR_WIDTH = 7;   // larghezza media di un carattere dei tick (12px)

/**
 * Larghezza del contenitore, aggiornata al resize (il grafico viene disegnato in pixel reali,
 * così testi e colonne restano leggibili anche su smartphone).
 */
function useWidth(ref) {
    const [width, setWidth] = useState(0);

    useLayoutEffect(() => {
        const element = ref.current;
        if (!element) return undefined;

        setWidth(element.clientWidth);
        if (typeof ResizeObserver === 'undefined') return undefined;

        const observer = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref]);

    return width;
}

// scala con valori "puliti" (0, 5, 10, 15...) e solo numeri interi
function niceScale(max, count = 4) {
    if (max <= 0) return { max: 1, ticks: [0, 1] };

    const raw = max / count;
    const power = Math.pow(10, Math.floor(Math.log10(raw)));
    const n = raw / power;
    const step = Math.max(1, (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * power);
    const niceMax = Math.ceil(max / step) * step;
    const ticks = [];
    for (let t = 0; t <= niceMax + step / 2; t += step) ticks.push(t);

    return { max: niceMax, ticks };
}

// colonna con angoli arrotondati solo in alto (la base resta dritta sulla linea dello zero)
function roundedTop(x, y, width, height, radius) {
    const r = Math.min(radius, height, width / 2);
    return `M${x},${y + height}V${y + r}Q${x},${y} ${x + r},${y}H${x + width - r}Q${x + width},${y} ${x + width},${y + r}V${y + height}Z`;
}

/**
 * Grafico a colonne (impilate se ci sono più serie).
 *
 * @param {String[]} categories etichette brevi dell'asse X
 * @param {String[]} tooltipLabels etichette complete per il tooltip (opzionale)
 * @param {Array} series [{ key, label, color, values: number[] }]
 * @param {Function} formatValue formattazione dei valori nel tooltip
 */
export default function ColumnChart({ categories, tooltipLabels, series, formatValue = formatNumber, height = 240 }) {
    const ref = useRef(null);
    const width = useWidth(ref);
    const [hover, setHover] = useState(null);

    const totals = useMemo(
        () => categories.map((_, i) => series.reduce((sum, serie) => sum + (serie.values[i] || 0), 0)),
        [categories, series]
    );

    const scale = useMemo(() => niceScale(Math.max(0, ...totals)), [totals]);
    const peakIndex = totals.indexOf(Math.max(...totals));

    const left = Math.max(...scale.ticks.map(t => formatCompact(t).length)) * CHAR_WIDTH + 10;
    const plotWidth = Math.max(0, width - left - MARGIN.right);
    const plotHeight = height - MARGIN.top - MARGIN.bottom;
    const band = categories.length ? plotWidth / categories.length : 0;
    const barWidth = Math.max(2, Math.min(MAX_BAR, band * 0.7));

    const y = (value) => MARGIN.top + plotHeight - (value / scale.max) * plotHeight;

    // mostro un'etichetta dell'asse X ogni N colonne, in base allo spazio disponibile
    const longestLabel = Math.max(...categories.map(c => String(c).length));
    const labelEvery = band ? Math.max(1, Math.ceil((longestLabel * CHAR_WIDTH + 8) / band)) : 1;

    const hovered = hover !== null && hover < categories.length ? hover : null;
    const tooltipLeft = hovered !== null ? Math.min(Math.max(left + band * hovered + band / 2, 90), width - 90) : 0;

    return (
        <div className="stColumnChart">
            {series.length > 1 && <Legend items={series} shape="rect" />}

            <div className="stPlot" ref={ref} onPointerLeave={() => setHover(null)}>
                {
                    width > 0 &&
                    <svg width={width} height={height} role="img" aria-label="Grafico a colonne, i valori sono consultabili anche nella vista tabella">
                        {
                            scale.ticks.map(tick => (
                                <g key={tick}>
                                    <line className={tick === 0 ? 'stAxis' : 'stGridLine'} x1={left} x2={width - MARGIN.right} y1={y(tick)} y2={y(tick)} />
                                    <text className="stTick" x={left - 8} y={y(tick)} textAnchor="end" dominantBaseline="middle">{formatCompact(tick)}</text>
                                </g>
                            ))
                        }

                        {
                            categories.map((category, i) => {
                                const x = left + band * i + (band - barWidth) / 2;
                                const visible = series.map(serie => ({ serie, value: serie.values[i] || 0 })).filter(s => s.value > 0);
                                let cumulative = 0;

                                return (
                                    <g key={category + i}>
                                        {hovered === i && <rect className="stHoverBand" x={left + band * i} y={MARGIN.top} width={band} height={plotHeight} />}

                                        {
                                            visible.map(({ serie, value }, k) => {
                                                const bottom = y(cumulative) - (k > 0 ? GAP : 0);
                                                cumulative += value;
                                                const top = y(cumulative);
                                                const segmentHeight = Math.max(1, bottom - top);

                                                return k === visible.length - 1
                                                    ? <path key={serie.key} d={roundedTop(x, bottom - segmentHeight, barWidth, segmentHeight, 4)} fill={serie.color} />
                                                    : <rect key={serie.key} x={x} y={bottom - segmentHeight} width={barWidth} height={segmentHeight} fill={serie.color} />;
                                            })
                                        }

                                        {
                                            (i % labelEvery === 0) &&
                                            <text className="stTick" x={left + band * i + band / 2} y={height - 8} textAnchor="middle">{category}</text>
                                        }

                                        {/* etichetta diretta solo sul valore massimo */}
                                        {
                                            i === peakIndex && totals[i] > 0 &&
                                            <text className="stPeak" x={left + band * i + band / 2} y={y(totals[i]) - 6} textAnchor="middle">{formatValue(totals[i])}</text>
                                        }

                                        {/* area di hover/tap più grande della colonna */}
                                        <rect
                                            className="stHit"
                                            x={left + band * i}
                                            y={MARGIN.top}
                                            width={band}
                                            height={plotHeight}
                                            tabIndex={0}
                                            aria-label={`${tooltipLabels ? tooltipLabels[i] : category}: ${formatValue(totals[i])}`}
                                            onPointerEnter={() => setHover(i)}
                                            onPointerDown={() => setHover(i)}
                                            onFocus={() => setHover(i)}
                                            onBlur={() => setHover(null)}
                                        />
                                    </g>
                                );
                            })
                        }
                    </svg>
                }

                {
                    hovered !== null &&
                    <div className="stTooltip" style={{ left: tooltipLeft, top: Math.max(0, y(totals[hovered]) - 12) }}>
                        <div className="stTooltipTitle">{tooltipLabels ? tooltipLabels[hovered] : categories[hovered]}</div>
                        {
                            series.map(serie => (
                                <div className="stTooltipRow" key={serie.key}>
                                    <span className="stLineKey" style={{ background: serie.color }} />
                                    <b>{formatValue(serie.values[hovered] || 0)}</b>
                                    {series.length > 1 && <span>{serie.label}</span>}
                                </div>
                            ))
                        }
                        {
                            series.length > 1 &&
                            <div className="stTooltipRow stTooltipTotal">
                                <b>{formatValue(totals[hovered])}</b><span>Totale</span>
                            </div>
                        }
                    </div>
                }
            </div>
        </div>
    );
}
